/**
 * @file DeliveryDestinationFields.tsx
 * @description Секция «Куда везём» формы расчёта доставки (phase19.12 шаг 5).
 *
 * Режим (ПВЗ / курьер), выбранный пункт выдачи и адресные поля. Значения
 * предзаполнены из заказа: правки здесь влияют только на расчёт, адрес заказа
 * остаётся прежним.
 */

import { useState } from "react";

import { DeliveryPickupPointCard } from "./DeliveryPickupPointCard";
import { SdekPickupPointPicker } from "../orders/SdekPickupPointPicker";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import type { DeliveryQuoteFormValue } from "../../lib/delivery-quote";

interface Props {
  value: DeliveryQuoteFormValue;
  onChange: (patch: Partial<DeliveryQuoteFormValue>) => void;
  /** ПВЗ из заказа — для отметки «тот же, что выбрал клиент». */
  clientPickupPointCode?: string | null;
  disabled?: boolean;
}

const MODES = [
  { key: "PVZ", label: "В пункт выдачи" },
  { key: "COURIER", label: "Курьером до двери" },
] as const;

export function DeliveryDestinationFields({
  value,
  onChange,
  clientPickupPointCode,
  disabled,
}: Props) {
  const [pickerOpen, setPickerOpen] = useState(false);

  return (
    <section className="flex flex-col gap-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
        Куда везём
      </div>

      <div className="flex flex-col gap-1">
        {MODES.map((opt) => (
          <label key={opt.key} className="flex min-h-11 items-center gap-2 text-sm">
            <input
              type="radio"
              name="delivery-quote-mode"
              checked={value.mode === opt.key}
              disabled={disabled}
              onChange={() => onChange({ mode: opt.key })}
            />
            {opt.label}
          </label>
        ))}
      </div>

      {value.mode === "PVZ" ? (
        <DeliveryPickupPointCard
          code={value.pickupPointCode}
          city={value.city}
          clientCode={clientPickupPointCode}
          disabled={disabled}
          onChangeClick={() => setPickerOpen(true)}
        />
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="delivery-quote-city">Город</Label>
          <Input
            id="delivery-quote-city"
            value={value.city}
            disabled={disabled}
            placeholder="Челябинск"
            onChange={(e) => onChange({ city: e.target.value, cityCode: null })}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="delivery-quote-postal">Индекс</Label>
          <Input
            id="delivery-quote-postal"
            inputMode="numeric"
            value={value.postalCode}
            disabled={disabled}
            placeholder="454000"
            onChange={(e) => onChange({ postalCode: e.target.value })}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="delivery-quote-address">Адрес</Label>
        <Input
          id="delivery-quote-address"
          value={value.address}
          disabled={disabled}
          placeholder="ул. Кирова, 12, кв. 5"
          onChange={(e) => onChange({ address: e.target.value })}
        />
        <p className="text-xs text-on-surface-variant">
          Из адреса заказа. Изменения тут — только для расчёта, адрес заказа не меняется.
        </p>
      </div>

      <SdekPickupPointPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(p) =>
          onChange({
            pickupPointCode: p.externalCode,
            city: p.city,
            cityCode: null,
            address: p.address,
          })
        }
      />
    </section>
  );
}
